import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  PhonenumberSetupCostParentAddForm,
  PhonenumberSetupCostParentAddInitialValues,
  PhonenumberSetupCostParentComponents,
  PhonenumberSetupCostParentAdditionalParams,
  PhonenumberSetupCostParentFormOptions,
  onPhonenumberSetupCostParentFormSaved,
  IPhonenumberSetupCostParentFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreatePhonenumberSetupCostParent, PermissionEnum, } from 'atlas-shared';
import { createPhonenumberSetupCostParent, fetchPhonenumberSetupCostParent, getPhonenumberSetupCostParentCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreatePhonenumberSetupCostParent> {}

const PhonenumberSetupCostParentFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreatePhonenumberSetupCostParent>>();

  const param_props: IPhonenumberSetupCostParentFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout = useMemo(() => PhonenumberSetupCostParentAddForm(values, param_props, t), [values, organizations, t, abilities, ]);
  const options: IFormOptions = useMemo(() => PhonenumberSetupCostParentFormOptions(values || PhonenumberSetupCostParentAddInitialValues, param_props, t), [values, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreatePhonenumberSetupCostParent, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreatePhonenumberSetupCostParent) => save(() => createPhonenumberSetupCostParent(values).then(
    res => onSaved ? onSaved(res, navigate) : onPhonenumberSetupCostParentFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchPhonenumberSetupCostParent(+id)
        .then(({
          title,
          currency,
          phonenumber_setup_cost_list,
        }) => {
          setCloned({
            title,
            currency,
            phonenumber_setup_cost_list,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(null, CrudEnum.Insert, PermissionEnum.PhonenumberSetupCostParent, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={PhonenumberSetupCostParentComponents}
        additionalParams={PhonenumberSetupCostParentAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...PhonenumberSetupCostParentAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('PHONENUMBER_SETUP_COST_PARENT')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const PhonenumberSetupCostParentFormAddView = React.memo(withAdd(PhonenumberSetupCostParentFormAdd, getPhonenumberSetupCostParentCreateSchema, PhonenumberSetupCostParentAddInitialValues));
